import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

import portfolio from '../../data';
import Reveal from '../ui/Reveal';
import SectionHeading from '../ui/SectionHeading';
import SpotlightCard from '../ui/SpotlightCard';

import '../../styles/Experience/ExperienceTimeline.scss';

const MAX_STACK = 7;

export default function ExperienceTimeline() {
  const jobs = portfolio.portfolio.experience;
  const listRef = useRef(null);

  // Fill the timeline line as the user scrolls through the section.
  useEffect(() => {
    let frame;
    const update = () => {
      const el = listRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const anchor = window.innerHeight * 0.55;
      const p = Math.min(1, Math.max(0, (anchor - r.top) / r.height));
      el.style.setProperty('--progress', p.toFixed(4));
    };
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section className="section timeline" id="experience" aria-labelledby="experience-title">
      <div className="container timeline__layout">
        <div className="timeline__intro">
          <SectionHeading
            index="02"
            eyebrow="Experience"
            id="experience-title"
            title="Where I've"
            accent="made an impact."
            description="From a funded logistics start-up to the gates of one of the world's busiest airports."
          />
          <Reveal className="timeline__cta" delay={250}>
            <Link to="/about/experience" className="btn btn--ghost">
              See the detailed timeline <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </Link>
          </Reveal>
        </div>

        <div className="timeline__list" ref={listRef}>
          <span className="timeline__line" aria-hidden="true">
            <span />
          </span>
          <ol className="timeline__items">
            {jobs.map((job, i) => {
              const extra = job.stack.length - MAX_STACK;
              return (
                <li className="timeline__item" key={`${job.company}-${job.period}`}>
                  <span className={`timeline__dot${/present/i.test(job.period) ? ' timeline__dot--current' : ''}`} aria-hidden="true" />
                  <Reveal variant="right" className="timeline__reveal">
                    <SpotlightCard className="timeline__card">
                      <div className="timeline__top">
                        <span className="timeline__period">{job.period}</span>
                        {/present/i.test(job.period) && <span className="timeline__now">Current</span>}
                      </div>
                      <h3>{job.role}</h3>
                      <p className="timeline__company">
                        {job.companyUrl ? (
                          <a href={job.companyUrl} target="_blank" rel="noopener noreferrer">
                            {job.company}
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} aria-hidden="true" />
                          </a>
                        ) : (
                          job.company
                        )}
                        <span> · {job.location}</span>
                      </p>
                      {job.note && <p className="timeline__note">{job.note}</p>}

                      <ul className="timeline__projects">
                        {job.projects.map((project) => (
                          <li key={project.name}>
                            <strong>{project.name}</strong>
                            <span>{project.summary}</span>
                          </li>
                        ))}
                      </ul>

                      <ul className="timeline__stack" aria-label="Tech stack">
                        {job.stack.slice(0, MAX_STACK).map((s) => (
                          <li key={s} className="chip">
                            {s}
                          </li>
                        ))}
                        {extra > 0 && <li className="chip">+{extra}</li>}
                      </ul>
                    </SpotlightCard>
                  </Reveal>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
